import { memo, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Card,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import MeetingRoomRoundedIcon from "@mui/icons-material/MeetingRoomRounded";
import SensorDoorRoundedIcon from "@mui/icons-material/SensorDoorRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import PodcastsRoundedIcon from "@mui/icons-material/PodcastsRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Label,
} from "recharts";

/* ─────────────────────────────────────────────────────────
   THEME CONSTANTS — matches DoorActivityTimeline / DoorDeviceTable
───────────────────────────────────────────────────────── */
const GREEN = "#16a34a";
const RED = "#dc2626";
const AMBER = "#d97706";
const BLUE = "#2563eb";
const CARD_BORDER = "#dbe2ea";
const CARD_RADIUS = "14px";
const CARD_SHADOW = "0 1px 4px rgba(15,23,42,0.05)";

const cardSx = {
  background: "#ffffff",
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: CARD_RADIUS,
  p: { xs: 2, sm: 2.5, md: 3 },
  boxShadow: CARD_SHADOW,
  width: "100%",
  boxSizing: "border-box",
};

/* Approximate height (px) of one resident row card, used to size the
   scroll container so exactly `visibleRows` rows show before scrolling. */
const RESIDENT_ROW_HEIGHT = 100;
const RESIDENT_ROW_GAP = 12; // theme spacing(1.5) at 8px base

/* ─────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────── */
function minutesSince(ts, now) {
  return Math.max(0, Math.floor((now - ts) / 60));
}

/*
 * Displays the timestamp exactly as the backend sent it — NO timezone
 * conversion to the viewer's browser/local timezone. Uses getUTCHours /
 * getUTCMinutes (instead of toLocaleTimeString, which shifts the display
 * to whatever timezone the viewing browser is set to) so every viewer,
 * regardless of where they are, sees the same HH:MM for the same
 * timestamp.
 */
function formatTime(ts) {
  if (!ts) return "--";
  const date = new Date(ts * 1000);
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/*
 * A closed door now checks how long it was actually open for. If that
 * duration was longer than returnWindowMinutes, it's flagged as
 * "returnedLate" instead of a plain "returned" — previously any closed
 * door was labelled "returned" regardless of how long it took.
 */
function classify(person, now, returnWindowMinutes) {
  if (person.contact === "Closed") {
    const elapsed = Math.floor((person.closedAt - person.openedAt) / 60);
    return elapsed > returnWindowMinutes ? "returnedLate" : "returned";
  }
  const elapsed = minutesSince(person.openedAt, now);
  if (elapsed > returnWindowMinutes) return "overdue";
  if (elapsed >= returnWindowMinutes - 5) return "dueSoon";
  return "watch";
}

const STATUS_META = {
  overdue: { label: "Overdue", color: RED, bg: "#fef2f2" },
  dueSoon: { label: "Due soon", color: AMBER, bg: "#fffbeb" },
  returned: { label: "Returned", color: GREEN, bg: "#f0fdf4" },
  returnedLate: { label: "Returned late", color: RED, bg: "#fef2f2" },
  watch: { label: "Monitoring", color: BLUE, bg: "#eff6ff" },
};

/* ─────────────────────────────────────────────────────────
   SMALL SUBCOMPONENTS
────────────────────────────────────────────────────── */
function StatusChip({ status }) {
  const meta = STATUS_META[status];
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        background: meta.bg,
        color: meta.color,
        fontWeight: 700,
        fontSize: "0.98rem",
        height: 32,
        border: `1px solid ${meta.color}33`,
      }}
    />
  );
}

function KpiCard({ icon: Icon, label, value, target, color }) {
  return (
    <Card
      elevation={0}
      sx={{
        flex: 1,
        background: "#ffffff",
        border: "1px solid #dbe2ea",
        borderRadius: "12px",
        position: "relative",
        overflow: "visible",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",

        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-120%",
          width: "80%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
          transform: "skewX(-20deg)",
          transition: "0.7s",
        },

        "&:hover": {
          transform: "translateY(-5px)",
          borderColor: color,
          boxShadow: `0 10px 25px rgba(15,23,42,0.08), 0 0 0 1px ${color}20, 0 0 18px ${color}25`,
        },

        "&:hover::before": { left: "140%" },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* LEFT SIDE */}
        <Box sx={{ flex: 1 }}>
          {/* TITLE + ICON */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                color: "#111827",
                fontSize: "0.84rem",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {label}
            </Typography>

            <Box sx={{ color: color, display: "flex", alignItems: "center" }}>
              <Icon sx={{ fontSize: 21 }} />
            </Box>
          </Box>

          {/* SUBTITLE / TARGET TEXT */}
          <Typography
            sx={{
              color: "#9ca3af",
              fontSize: "0.88rem",
              fontWeight: 400,
              mt: 0.8,
            }}
          >
            Target: {target}
          </Typography>
        </Box>

        {/* VALUE */}
        <Box sx={{ textAlign: "center", minWidth: 60 }}>
          <span
            style={{
              color: "#111827",
              fontSize: "37px",
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: "-0.04em",
              display: "block",
            }}
          >
            {value ?? "NA"}
          </span>
        </Box>
      </Box>
    </Card>
  );
}

function RuleRow({ icon: Icon, color, bg, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderRadius: "10px",
        p: 1.5,
        background: bg,
        color: color,
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <Icon sx={{ fontSize: 23, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "1.06rem", fontWeight: 500 }}>{children}</Typography>
    </Box>
  );
}

/* Turns a bucket label like "20:30" into the 1-hour window it represents,
   e.g. "20:00 - 21:00", instead of showing a single point in time. */
function formatHourRange(hourLabel) {
  if (!hourLabel || typeof hourLabel !== "string") return hourLabel;
  const [hStr] = hourLabel.split(":");
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return hourLabel;
  const pad = (n) => String(n).padStart(2, "0");
  const nextH = (h + 1) % 24;
  return `${pad(h)}:00 - ${pad(nextH)}:00`;
}

/* Custom tooltip — shows the hour bucket as a range rather than a single
   timestamp, keeping the same open/overdue rows the default tooltip had. */
function HourlyChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <Box
      sx={{
        background: "#ffffff",
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: "10px",
        p: 1.5,
        boxShadow: "0 4px 14px rgba(15,23,42,0.12)",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "1.03rem", color: "#111827", mb: 0.5 }}>
        {formatHourRange(label)}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} sx={{ fontSize: "1.03rem", color: entry.color || "#374151" }}>
          {entry.name} : {entry.value}
        </Typography>
      ))}
    </Box>
  );
}

/* Isolated + memoized so the once-a-second clock tick (which only the
   resident list needs) never causes this chart to re-render or flicker.
   It only re-renders when the `hourlyEvents` array reference changes. */
const HourlyEventsChart = memo(function HourlyEventsChart({ hourlyEvents }) {
  return (
    <Box sx={cardSx}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "flex-start" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1.38rem" }}>
            Door events by hour (Last 24h)
          </Typography>
          <Typography sx={{ fontSize: "1.01rem", color: "#9ca3af", mt: 0.3, mb: 1 }}>
            Rolling volume and overdue patterns for staffing decisions
          </Typography>
        </Box>

        {/* Dummy, view-only for now — not wired to any search/filter logic.
            Styled to match the search box in the resident list below. */}
        <TextField
          size="small"
          placeholder="Search device"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 220 },
            flexShrink: 0,
            "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "1.06rem", background: "#f9fafb" },
          }}
        />
      </Box>
      <Box sx={{ height: { xs: 280, sm: 340, md: 400 }, mt: 1, width: "100%" }}>
        {hourlyEvents.length === 0 ? (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: "1.09rem" }}>No hourly data yet</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hourlyEvents} margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
  <XAxis
    dataKey="hour"
    tick={{ fontSize: 15, fill: "#6b7280" }}
    interval={1}
  >
    <Label
      value="Time (HH:MM)"
      position="bottom"
      offset={0}
      style={{ fontSize: 14, fill: "#374151", fontWeight: 600 }}
    />
  </XAxis>
  <YAxis tick={{ fontSize: 15, fill: "#6b7280" }} width={45}>
    <Label
      value="Number of Events"
      angle={-90}
      position="insideLeft"
      style={{ fontSize: 14, fill: "#374151", fontWeight: 600, textAnchor: "middle" }}
    />
  </YAxis>
  <RechartsTooltip content={<HourlyChartTooltip />} />
  <Bar dataKey="opens" name="Open events" fill={BLUE} radius={[6, 6, 0, 0]} />
  <Bar dataKey="overdue" name="Overdue" fill={RED} radius={[6, 6, 0, 0]} />
</BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
});

/* ─────────────────────────────────────────────────────────
   MAIN EXPORT (presentational — receives all data via props,
   nothing is hardcoded inside this component)
   Props:
     residents           — [{ id?, room, deviceId,
                              contact ("Open"|"Closed"),
                              openedAt (unix seconds), closedAt? }]
                            Only openedAt/closedAt/contact are expected
                            from the backend — elapsed time is always
                            computed here, never read from the payload.
                            While contact is "Closed" elapsed reads 0;
                            the moment it flips to "Open" the elapsed
                            counter starts from openedAt and keeps
                            running until the door closes again.
                            openedAt and closedAt are read straight from
                            the backend on every update: while contact is
                            "Open" only Open Time is shown; once contact
                            is "Closed" both Open Time and Return Time
                            (closedAt) are shown together.
                            Names are intentionally not shown in the UI
                            (masked as "xxxxx" for privacy) — pass a
                            resident id if you have one, it's only used
                            internally as a React key, never displayed.
     hourlyEvents         — [{ hour, opens, overdue }]
     responseTrend        — [{ day, avg, target }]
     returnWindowMinutes  — number, default 30
     site                 — string, shown in the hero banner
     visibleRows          — number of resident cards visible before the
                             list scrolls, default 5
     actionsDisabled      — whether the Dispatch/Review buttons are
                             disabled, default true
     loading              — bool
     error                — string | null
───────────────────────────────────────────────────────── */
export default function DoorReturnMonitoringDashboard({
  residents = [],
  hourlyEvents = [],
  responseTrend = [],
  returnWindowMinutes = 30,
  site = "",
  visibleRows = 5,
  actionsDisabled = true,
  loading = false,
  error = null,
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Live clock — ticks on its own so elapsed time and status
  // (watch → dueSoon at window-5 → overdue at window) update in real time,
  // following the operational rules continuously rather than only when new
  // data arrives from the parent. 15s is plenty since values only display
  // in whole minutes, and it keeps re-renders (and the flicker they caused)
  // way down compared to a 1-second tick.
  function getCurrentUKUnixTime() {
    const ukNow = new Date(
      new Date().toLocaleString("en-US", {
        timeZone: "Europe/London",
      })
    );
  
    return Math.floor(ukNow.getTime() / 1000);
  }
  
  const [clockNow, setClockNow] = useState(getCurrentUKUnixTime);
  
  useEffect(() => {
    const tick = setInterval(() => {
      setClockNow(getCurrentUKUnixTime());
    }, 15000);
  
    return () => clearInterval(tick);
  }, []);
  const now = clockNow;

  // Backend now reliably sends both openedAt and closedAt on every update,
  // so we read them straight from the payload — no need to remember or
  // patch in stale values anymore.
  const enriched = useMemo(
    () =>
      residents.map((p, idx) => {
        const key = p.id ?? idx;
        const { openedAt, closedAt, contact } = p;

        // elapsed time is always derived here, never trusted from the
        // payload. While the door is closed there is no active timer, so
        // elapsed reads 0. The moment contact flips to "Open", the
        // elapsed counter begins from openedAt and keeps running until
        // the door closes again.
        const isOpen = contact === "Open";

        let elapsed = 0;

        if (contact === "Closed" && openedAt && closedAt) {
          // Return Time - Open Time
          elapsed = Math.floor((closedAt - openedAt) / 60);
        } else if (isOpen) {
          // Still open -> Current Time - Open Time
          elapsed = minutesSince(openedAt, now);
        }

        // status now also flags a closed door that took longer than
        // returnWindowMinutes as "returnedLate" instead of a plain
        // "returned" (see classify()).
        const status = classify({ contact, openedAt, closedAt }, now, returnWindowMinutes);

        return { ...p, _key: key, openedAt, closedAt, contact, elapsed, status };
      }),
    [residents, returnWindowMinutes, now]
  );

  const filtered = enriched.filter((p) => {
    const textMatch = `${p.room} ${p.deviceId}`
      .toLowerCase()
      .includes(query.toLowerCase());
    const statusMatch = statusFilter === "all" || p.status === statusFilter;
    return textMatch && statusMatch;
  });

  const overdue = enriched.filter((p) => p.status === "overdue").length;
  const dueSoon = enriched.filter((p) => p.status === "dueSoon").length;
  const activeOpen = enriched.filter((p) => p.contact === "Open").length;
  const returned = enriched.filter((p) => p.status === "returned" || p.status === "returnedLate").length;

  // Size the resident list container so exactly `visibleRows` rows are
  // visible before the list becomes scrollable.
  const listMaxHeight = visibleRows * RESIDENT_ROW_HEIGHT + Math.max(visibleRows - 1, 0) * RESIDENT_ROW_GAP;
  const shouldScroll = filtered.length > visibleRows;

  // if (loading) {
  //   return (
  //     <Box sx={{ ...cardSx, display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
  //       <Typography sx={{ color: "#9ca3af", fontSize: "0.98rem" }}>Loading…</Typography>
  //     </Box>
  //   );
  // }

  // if (error) {
  //   return (
  //     <Box sx={{ ...cardSx, display: "flex", justifyContent: "center", alignItems: "center", height: 320 }}>
  //       <Typography sx={{ color: RED }}>{error}</Typography>
  //     </Box>
  //   );
  // }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 2 }, width: "100%", boxSizing: "border-box" }}>
      {/* ── HERO BANNER ── */}
      <Box
        sx={{
          background: "#0f172a",
          borderRadius: CARD_RADIUS,
          p: { xs: 2, sm: 2.5, md: 3 },
          boxShadow: CARD_SHADOW,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2,
          color: "#ffffff",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <Chip
              icon={<PodcastsRoundedIcon sx={{ color: "#ffffff !important", fontSize: 17 }} />}
              label="Live"
              size="small"
              sx={{ background: GREEN, color: "#fff", fontWeight: 700, fontSize: "0.9rem" }}
            />
            <Chip
              label={`Return window: ${returnWindowMinutes} min`}
              size="small"
              sx={{ background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: "0.9rem" }}
            />
            {site && (
              <Chip
                label={site}
                size="small"
                sx={{ background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: "0.9rem" }}
              />
            )}
          </Box>
          <Typography sx={{ mt: 1.5, fontSize: { xs: "1.6rem", sm: "1.95rem", md: "2.4rem" }, fontWeight: 800 }}>
            Door Return Monitoring
          </Typography>
          <Typography sx={{ mt: 1, fontSize: "1.13rem", color: "#cbd5e1", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Prioritise residents who opened a door and have not returned within the expected time.
            Elapsed time and escalation for dispatchers and first responders.
          </Typography>
        </Box>
      </Box>

      {/* ── OPERATIONAL RULES (2x2 matrix) ── */}
      <Box sx={cardSx}>
        <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1.32rem", mb: 2 }}>
          Operational rules
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gridAutoRows: "1fr",
            gap: 1.2,
          }}
        >
          <RuleRow icon={NotificationsRoundedIcon} color="#1e40af" bg="#eff6ff">
            Door open starts a {returnWindowMinutes}-minute return timer.
          </RuleRow>
          <RuleRow icon={TimerRoundedIcon} color="#92400e" bg="#fffbeb">
            At {Math.max(returnWindowMinutes - 5, 0)} minutes, notify assigned responder.
          </RuleRow>
          <RuleRow icon={CancelRoundedIcon} color="#991b1b" bg="#fef2f2">
            After {returnWindowMinutes} minutes, escalate to command dashboard.
          </RuleRow>
          <RuleRow icon={CheckCircleRoundedIcon} color="#166534" bg="#f0fdf4">
            Door closed within window marks safe return, pending audit.
          </RuleRow>
        </Box>
      </Box>

      {/* ── KPI CARDS ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: { xs: 1.25, sm: 1.5, md: 2 },
        }}
      >
        <KpiCard icon={WarningAmberRoundedIcon} label="Overdue " value={overdue} target="0 active" color={RED} bg="#fef2f2" />
        <KpiCard icon={AccessTimeRoundedIcon} label="Due in 5 min" value={dueSoon} target="Triage < 2 min" color={AMBER} bg="#fffbeb" />
        <KpiCard icon={MeetingRoomRoundedIcon} label="Open-door cases" value={activeOpen} target="100% tracked" color={BLUE} bg="#eff6ff" />
        <KpiCard icon={VerifiedUserRoundedIcon} label="Returned safely" value={returned} target="≥ 95%" color={GREEN} bg="#f0fdf4" />
      </Box>

      {/* ── LIVE MONITORED RESIDENTS ── */}
      <Box sx={cardSx}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1.32rem" }}>
              Live monitored residents(Last 24h)
            </Typography>
            <Typography sx={{ fontSize: "0.94rem", color: "#9ca3af", mt: 0.3 }}>
              {enriched.length} tracked · showing {Math.min(filtered.length, visibleRows)} of {filtered.length} at a time, scroll for more
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              gap: 1,
              width: { xs: "100%", md: "auto" },
            }}
          >
            <TextField
              size="small"
              placeholder="Search room, device"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 220 },
                "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "1.06rem", background: "#f9fafb" },
              }}
            />

            <FormControl size="small" sx={{ width: { xs: "100%", sm: 160 } }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <FilterListRoundedIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                  </InputAdornment>
                }
                sx={{ borderRadius: "8px", fontSize: "1.06rem", background: "#f9fafb" }}
              >
                <MenuItem value="all">All status</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="dueSoon">Due soon</MenuItem>
                <MenuItem value="watch">Monitoring</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
                <MenuItem value="returnedLate">Returned late</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxHeight: shouldScroll ? `${listMaxHeight}px` : "none",
            overflowY: shouldScroll ? "auto" : "visible",
            pr: shouldScroll ? 0.5 : 0,
            // subtle scrollbar styling
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { background: "#d1d5db", borderRadius: 3 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
          }}
        >
          {filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography sx={{ color: "#9ca3af", fontSize: "0.98rem" }}>
                {enriched.length === 0 ? "No residents are currently being tracked" : "No residents match the current filters"}
              </Typography>
            </Box>
          ) : (
            filtered.map((p) => {
              const meta = STATUS_META[p.status];
              const hasClosed = p.status === "returned" || p.status === "returnedLate";

              return (
                <Box
                  key={p._key}
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    p: { xs: 1.5, sm: 2 },
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1.5,
                    transition: "box-shadow 0.15s, border-color 0.15s",
                    flexShrink: 0,
                    "&:hover": { boxShadow: "0 2px 8px rgba(15,23,42,0.06)", borderColor: "#d1d5db" },
                  }}
                >
                  {/* Info column: ~half the card, name + room + device */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: "1 1 320px" }}>
                    <Box
                      sx={{
                        borderRadius: "12px",
                        p: 1.2,
                        background: meta.bg,
                        color: meta.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {p.contact === "Open" ? (
                        <MeetingRoomRoundedIcon sx={{ fontSize: 25 }} />
                      ) : (
                        <SensorDoorRoundedIcon sx={{ fontSize: 25 }} />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.27rem", color: "#111827", letterSpacing: "1.5px" }}>
                          xxxxx
                        </Typography>
                        <StatusChip status={p.status} />
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 3, mt: 0.5 }}>
                        <Typography sx={{ fontSize: "1.09rem", color: "#6b7280", wordBreak: "break-word" }}>
                          Room - {p.room}
                        </Typography>
                        <Typography sx={{ fontSize: "1.09rem", color: "#6b7280", wordBreak: "break-word" }}>
                          Device ID: {p.deviceId}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Stats column: ~half the card.
                      Order: Open Time → Return Time (shown once the door
                      has closed again, whether on time or late) →
                      Elapsed → Action button */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: hasClosed
                        ? "repeat(4, minmax(120px, 1fr))"
                        : "repeat(3, minmax(120px, 1fr))",
                      gap: 1.25,
                      flex: "1 1 420px",
                    }}
                  >
                    {/* Open Time */}
                    <Box
                      sx={{
                        background: "#eff6ff",
                        borderRadius: "10px",
                        p: 1.3,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.92rem", color: BLUE }}>
                        Open Time
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "1.32rem",
                          fontWeight: 700,
                          color: BLUE,
                        }}
                      >
                        {formatTime(p.openedAt)}
                      </Typography>
                    </Box>

                    {/* Return Time — shown once the door has closed again,
                        whether it closed on time (returned) or late
                        (returnedLate) */}
                    {hasClosed && (
                      <Box
                        sx={{
                          background: "#f0fdf4",
                          borderRadius: "10px",
                          p: 1.3,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.92rem", color: GREEN }}>
                          Return Time
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "1.32rem",
                            fontWeight: 700,
                            color: GREEN,
                          }}
                        >
                          {formatTime(p.closedAt)}
                        </Typography>
                      </Box>
                    )}

                    {/* Elapsed */}
                    <Box
                      sx={{
                        background: "#f9fafb",
                        borderRadius: "10px",
                        p: 1.3,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.92rem", color: "#9ca3af" }}>
                        Elapsed
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "1.32rem",
                          fontWeight: 700,
                        }}
                      >
                        {p.elapsed}m
                      </Typography>
                    </Box>

                    {/* Button */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={actionsDisabled}
                      >
                        {hasClosed ? "Review" : "Dispatch"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* ── CHARTS ── */}
      <HourlyEventsChart hourlyEvents={hourlyEvents} />
    </Box>
  );
}
